/*!
 * AccessibleWeb Widget
 * (version is injected into dist builds from package.json at build time)
 * https://github.com/ifrederico/accessible-web-widget
 *
 * Copyright (c) 2025 ifrederico
 * Released under the MIT License
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND.
 * See DISCLAIMER.md for important legal information.
 * This widget does not guarantee accessibility compliance.
 */

import { WIDGET_THEME } from './constants/theme.js';
import { WIDGET_ICONS } from './constants/icons.js';
import { TARGET_SELECTORS, PAGE_CONTENT_SELECTOR } from './constants/selectors.js';
import { TRANSLATIONS, SUPPORTED_LANGUAGES } from './constants/translations.js';
import { stateMethods } from './state.js';
import { styleMethods } from './styles.js';
import { featureMethods } from './features.js';
import { uiMethods } from './ui.js';

class AccessibleWebWidget {
  constructor(options = {}) {
    // ======================================================
    // CONFIGURATION & INITIAL STATE
    // ======================================================
    this.widgetTheme = { ...WIDGET_THEME };
    this.widgetIcons = WIDGET_ICONS;
    this.targetSelectors = TARGET_SELECTORS;

    this.visualFilters = {
      'dark-contrast': {
        styles: { 'filter': 'contrast(150%) brightness(0.8)' },
        selector: PAGE_CONTENT_SELECTOR
      },
      'light-contrast': {
        styles: { 'filter': 'contrast(125%) brightness(1.2)' },
        selector: PAGE_CONTENT_SELECTOR
      },
      'invert-colors': {
        styles: { 'filter': 'invert(100%)' },
        selector: PAGE_CONTENT_SELECTOR
      },
      'low-saturation': {
        styles: { 'filter': 'saturate(50%)' },
        selector: PAGE_CONTENT_SELECTOR
      },
      'high-saturation': {
        styles: { 'filter': 'saturate(200%)' },
        selector: PAGE_CONTENT_SELECTOR
      },
      'monochrome': {
        styles: { 'filter': 'grayscale(100%)' },
        selector: PAGE_CONTENT_SELECTOR
      }
    };

    this.translations = TRANSLATIONS;
    this.supportedLanguages = SUPPORTED_LANGUAGES.map((language) => ({ ...language }));

    this.accessTools = [
      // Hidden on touch-only devices, where there is no cursor to enlarge.
      { label: 'Big Cursor', key: 'large-pointer', icon: this.widgetIcons.largePointer, requiresFinePointer: true },
      { label: 'Stop Animations', key: 'pause-motion', icon: this.widgetIcons.pauseMotion },
      { label: 'Reading Guide', key: 'reading-aid', icon: this.widgetIcons.readingAid },
      {
        label: 'Text to Speech',
        key: 'text-to-speech',
        icon: this.widgetIcons.textToSpeech,
        requiresSpeechSynthesis: true
      },
      { label: 'High Contrast', key: 'high-contrast-mode', icon: this.widgetIcons.highContrast },
      { label: 'Simplify Layout', key: 'simple-layout', icon: this.widgetIcons.simplifyLayout },
      { label: 'Mute Sounds', key: 'mute-sounds', icon: this.widgetIcons.muteSounds },
      { label: 'Text Magnifier', key: 'text-magnifier', icon: this.widgetIcons.textMagnifier },
      {
        label: 'Page Structure',
        key: 'page-structure',
        icon: this.widgetIcons.pageStructure,
        isAction: true
      }
    ];

    // One-tap presets bundling existing features. Each profile persists its
    // own state key plus the bundled feature states.
    this.accessibilityProfiles = [
      {
        label: 'Seizure Safe',
        key: 'profile-seizure-safe',
        icon: this.widgetIcons.pauseMotion,
        states: { 'pause-motion': true, 'low-saturation': true }
      },
      {
        label: 'Vision Impaired',
        key: 'profile-vision',
        icon: this.widgetIcons.highContrast,
        states: { 'text-scale': 1.4, 'high-contrast-mode': true, 'large-pointer': true }
      },
      {
        label: 'ADHD Friendly',
        key: 'profile-adhd',
        icon: this.widgetIcons.readingAid,
        states: { 'reading-aid': true, 'pause-motion': true }
      },
      {
        label: 'Dyslexia Friendly',
        key: 'profile-dyslexia',
        icon: this.widgetIcons.dyslexiaFont,
        states: { 'readable-text': 'dyslexic', 'line-spacing': true, 'letter-spacing': true }
      }
    ];

    // Add dev-only tools (?acc-dev=true)
    if (this.isDevMode()) {
      this.accessTools.push(
        { label: 'Annotations', key: 'annotations', icon: this.widgetIcons.annotations },
        {
          label: 'Accessibility Report',
          key: 'accessibility-report',
          icon: this.widgetIcons.accessibilityReport,
          isAction: true
        }
      );
    }

    // axe-core state
    this.axeCoreLoaded = false;
    this.axeCorePromise = null;
    this.axeScanResults = null;
    this.axeScanPromise = null;
    this.violationBubble = null;

    // Accessibility report modal state
    this.reportPanel = null;
    this.reportPreviousFocus = null;
    this.reportKeyListener = null;

    // System preference state
    this.systemPreferenceListeners = [];
    this.systemPreferenceMediaQueries = {};

    // Annotation state
    this.annotationLayer = null;
    this.annotationItems = [];
    this.annotationPopup = null;
    this.annotationRepositionHandler = null;
    this.annotationOutsideHandler = null;
    this.annotationRequestId = 0;

    // Text-to-speech state
    this.ttsUtterance = null;
    this.ttsClickListener = null;
    this.ttsActiveTarget = null;
    this.ttsTextCache = '';
    this.ttsStatus = 'stopped';
    this.ttsQueue = [];
    this.ttsQueueIndex = 0;
    this.ttsSessionId = 0;
    this.ttsVoice = null;


    // Simplify layout state
    this.simpleLayoutRoot = null;
    this.simpleLayoutHiddenElements = [];

    // Track direct user toggles for features that have side effects.
    this.userInitiatedToggleKey = null;

    // Font size slider bounds
    this.textScaleMinPercent = 80;
    this.textScaleMaxPercent = 150;
    this.textScaleStepPercent = 5;
    this.contrastFilterValues = ['light-contrast', 'dark-contrast'];
    this.saturationFilterValues = ['low-saturation', 'high-saturation', 'monochrome'];

    // Readable Font levels; the persisted readable-text state holds the
    // active key ('dyslexic' | 'legible' | 'lexend'; legacy true maps to
    // 'dyslexic'). Labels are translation keys except the Lexend brand name.
    this.readableFontChoices = [
      { key: 'dyslexic', label: 'Dyslexia Font', family: '"OpenDyslexic3", "Comic Sans MS", Arial, Helvetica, sans-serif' },
      { key: 'legible', label: 'Legible Font', family: '"Atkinson Hyperlegible", Verdana, Arial, Helvetica, sans-serif' },
      { key: 'lexend', label: 'Lexend', family: '"Lexend", "Segoe UI", Roboto, Arial, sans-serif' }
    ];

    this.contentOptions = [
      { label: 'Font Weight', key: 'bold-text', icon: this.widgetIcons.boldText },
      { label: 'Line Height', key: 'line-spacing', icon: this.widgetIcons.lineSpacing },
      { label: 'Letter Spacing', key: 'letter-spacing', icon: this.widgetIcons.letterSpacing },
      { label: 'Hide Images', key: 'hide-images', icon: this.widgetIcons.hideImages },
      {
        label: 'Readable Font',
        key: 'readable-text',
        icon: this.widgetIcons.dyslexiaFont,
        multiLevel: true,
        levels: this.readableFontChoices.length
      },
      { label: 'Highlight Links', key: 'highlight-links', icon: this.widgetIcons.highlightLinks },
      { label: 'Highlight Title', key: 'highlight-title', icon: this.widgetIcons.highlightTitle },
      { label: 'Font Size', key: 'text-scale', icon: this.widgetIcons.adjustFontSize }
    ];

    this.multiLevelFeatures = {
      'contrast-toggle': {
        levels: this.contrastFilterValues.length,
        currentIndex: -1,
        values: this.contrastFilterValues
      },
      'saturation-toggle': {
        levels: this.saturationFilterValues.length,
        currentIndex: -1,
        values: this.saturationFilterValues
      },
      'readable-text': {
        levels: this.readableFontChoices.length,
        currentIndex: -1,
        values: this.readableFontChoices.map((choice) => choice.key)
      }
    };

    this.readingAidTemplate = `
      <div class="acc-rg acc-rg-top" role="presentation"> </div>
      <div class="acc-rg acc-rg-bottom" role="presentation"> </div>
    `;

    this.colorOptions = [
      {
        label: 'Contrast',
        key: 'contrast-toggle',
        icon: this.widgetIcons.contrast,
        multiLevel: true,
        levels: this.contrastFilterValues.length
      },
      { label: 'Invert Colors', key: 'invert-colors', icon: this.widgetIcons.invertColors },
      {
        label: 'Saturation',
        key: 'saturation-toggle',
        icon: this.widgetIcons.saturation,
        multiLevel: true,
        levels: this.saturationFilterValues.length
      }
    ];
    this.colorFilterKeys = Object.keys(this.visualFilters);
    this.activeColorFilterKey = null;

    this.textScaleSelectors = 'h1,h2,h3,h4,h5,h6,p,a,dl,dt,li,ol,th,td,span,blockquote,.acc-text';
    this.textScaleObserver = null;
    this.currentTextScaleMultiplier = 1;

    // Global settings and state
    this.widgetConfig = {};
    this.cookieKey = 'accweb';
    this.readingAidVisible = false;
    this.readingGuideY = null;
    this.readingGuideListeners = null;
    this.readableFontsLoaded = new Set();

    // Shadow DOM host for the widget UI
    this.shadowHost = null;
    this.widgetRoot = null;

    // Screen-reader live region
    this.liveRegion = null;
    this.liveRegionTimer = null;

    // Mute sounds state
    this.muteSoundsObserver = null;

    // Stop Animations media state (paused videos / frozen GIFs)
    this.motionPauseObserver = null;
    this.frozenGifs = [];

    // Text magnifier state
    this.magnifierElement = null;
    this.magnifierMoveHandler = null;
    this.magnifierDownHandler = null;
    this.magnifierScrollHandler = null;
    this.magnifierLastPointerType = null;

    // Page structure panel state
    this.structurePanel = null;

    // Menu state tracking for focus management
    this.activeMenuContainer = null;
    this.activeMenuToggle = null;
    this.menuKeyListener = null;
    this.previousFocus = null;
    // Last input was a pointer (tap/click) rather than the keyboard.
    this.lastInputWasPointer = false;
    this.widgetToggleButton = null;
    this.skipLinkElement = null;
    this.menuContainer = null;
    this.widgetContainerEl = null;
    this.launchOptions = null;

    // Style registration state
    this.staticStylesRegistered = false;
    // id → CSSStyleSheet for the constructable-stylesheet injection path
    this.adoptedSheets = new Map();
    this.styleNonce = '';

    this.dataOptions = this.getDataAttributeOptions();

    // `lang` is intentionally not defaulted here: startAccessibleWebWidget()
    // resolves it as saved user pick → embed config → <html lang> → browser.
    this.options = {
      position: 'bottom-right',
      ...this.dataOptions,
      ...options
    };

    this.applyThemeOverrides(this.options);

    // CSP nonce for the <style>-element fallback path (options.nonce,
    // data-acc-nonce, or auto-captured from document.currentScript).
    this.styleNonce = typeof this.options.nonce === 'string' ? this.options.nonce.trim() : '';

    // Raw values; getNativeTtsRate()/getNativeTtsPitch() clamp on read.
    this.nativeTtsConfig = {
      preferredVoiceName: (
        typeof options.ttsNativeVoiceName === 'string' &&
        options.ttsNativeVoiceName.trim()
      ) ? options.ttsNativeVoiceName.trim() : '',
      preferredVoiceLang: (
        typeof options.ttsNativeVoiceLang === 'string' &&
        options.ttsNativeVoiceLang.trim()
      ) ? options.ttsNativeVoiceLang.trim() : '',
      rate: options.ttsRate,
      pitch: options.ttsPitch
    };

    if (this.options.offset) {
      this.options.offset = this.normalizeOffset(this.options.offset);
    }

    if (this.options.size) {
      this.widgetTheme.buttonSize = this.normalizeButtonSize(this.options.size);
      this.options.size = this.widgetTheme.buttonSize;
    }

    this.applyThemeVariables();
    this.registerStaticStyles();
  }

  applyThemeOverrides(options = {}) {
    if (!options || typeof options !== 'object') return;

    const mergedOptions = {
      ...(options.theme && typeof options.theme === 'object' ? options.theme : {}),
      ...options
    };

    const themeKeys = [
      'primaryColor',
      'primaryColorLight',
      'primaryColorDark',
      'backgroundColor',
      'textColor',
      'textColorInverted',
      'cardBackground',
      'borderColor',
      'focusRingColor',
      'hoverColor',
      'activeColor',
      'borderRadius',
      'buttonBorderRadius',
      'headerHeight',
      'focusBorderWidth',
      'focusOutlineOffset',
      'zIndex',
      'menuPosition'
    ];

    themeKeys.forEach((key) => {
      const value = mergedOptions[key];
      if (value === undefined || value === null) return;

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return;
        this.widgetTheme[key] = trimmed;
        return;
      }

      this.widgetTheme[key] = value;
    });

    if (mergedOptions.buttonSize !== undefined && mergedOptions.buttonSize !== null && mergedOptions.buttonSize !== '') {
      this.widgetTheme.buttonSize = this.normalizeButtonSize(mergedOptions.buttonSize);
    }
  }
}

/** @typedef {typeof stateMethods & typeof styleMethods & typeof featureMethods & typeof uiMethods} WidgetMixedMethods */
/** @typedef {AccessibleWebWidget & WidgetMixedMethods} AccessibleWebWidgetInstance */

/** @type {AccessibleWebWidget['prototype'] & WidgetMixedMethods} */
const widgetPrototype = AccessibleWebWidget.prototype;

Object.assign(
  widgetPrototype,
  stateMethods,
  styleMethods,
  featureMethods,
  uiMethods
);

if (typeof window !== 'undefined') {
  // A second copy of the script re-evaluates this module and replaces the
  // global; carry the already-started instance over so the public API
  // (AccessibleWebWidget.instance.open()) keeps pointing at the live widget.
  const previousGlobal = window.AccessibleWebWidget;
  window.AccessibleWebWidget = AccessibleWebWidget;
  if (previousGlobal?.instance) {
    AccessibleWebWidget.instance = previousGlobal.instance;
  }
}

if (typeof document !== 'undefined') {
  const configuredOptions = (
    typeof window !== 'undefined' &&
    window.AccessibleWebWidgetOptions &&
    typeof window.AccessibleWebWidgetOptions === 'object'
  ) ? window.AccessibleWebWidgetOptions : {};

  // document.currentScript is only valid during initial script evaluation,
  // so the CSP nonce has to be captured here, not at DOMContentLoaded.
  const scriptNonce = (
    document.currentScript &&
    typeof document.currentScript.nonce === 'string'
  ) ? document.currentScript.nonce : '';
  const globalAutoInitOptions = (scriptNonce && configuredOptions.nonce === undefined)
    ? { ...configuredOptions, nonce: scriptNonce }
    : configuredOptions;

  /** @type {AccessibleWebWidgetInstance} */
  const widgetInstance = new AccessibleWebWidget(globalAutoInitOptions);
  // Programmatic access for host pages: AccessibleWebWidget.instance.open()
  // / .close() / .toggle() (pairs with the hideButton option). Never clobber
  // an instance that a first copy of the script already started — this one's
  // startAccessibleWebWidget() will see the existing host and no-op.
  if (!AccessibleWebWidget.instance) {
    AccessibleWebWidget.instance = widgetInstance;
  }
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    widgetInstance.startAccessibleWebWidget();
  } else {
    document.addEventListener('DOMContentLoaded', () => widgetInstance.startAccessibleWebWidget());
  }
}

export default AccessibleWebWidget;
